/**
 * Rehearsal series editor (sheet). Modes:
 *  - create: new series (POST /api/series)
 *  - following: "Þessi og næstu" from an occurrence date. When the weekdays/interval are unchanged
 *    and no not-yet-started occurrence precedes that date, PATCH /api/series/:id updates every
 *    future occurrence in place (RSVPs and comments are kept). The server skips occurrences that
 *    were edited on their own ("Aðeins þessi"), so when the opened occurrence is one of those, the
 *    changed fields are also applied to it with PATCH /api/events/:id. Otherwise
 *    POST /api/series/:id/split ends the old series the day before and starts a new one from that date.
 * Mount it only while open (form state initialises on mount).
 */
import { useQueryClient } from '@tanstack/react-query';
import { Repeat } from 'lucide-react';
import { useState } from 'react';
import { groupsFromMask, maskFromGroups, type AudienceGroup } from '../../../shared/audience';
import type { EventDTO, EventKind, SeriesDTO, UpsertSeriesRequest } from '../../../shared/dto/events';
import { uuid } from '../../../shared/ids';
import { dateTimeToMs } from '../../../shared/time';
import { hasUpcomingOccurrenceBefore, useCalendarMutations, type EventPatch, type SeriesPatch } from '../../api/calendar';
import { serverNow } from '../../api/client';
import { useToast } from '../../components/toast';
import { Button, ErrorBox, Sheet, TextArea, TextInput } from '../../components/ui';
import { useI18n, useT } from '../../i18n';
import { durationBetween, firstSeriesDate, formatDate, hhmmFromMinutes, isoWeekday, minutesOf, todayStr } from '../../lib/dates';
import {
  AudienceChips,
  CheckinOpensSelect,
  DESCRIPTION_MAX,
  IntervalChips,
  KindChips,
  LOCATION_MAX,
  seriesSummary,
  SetlistSelect,
  TimeRangeFields,
  TITLE_MAX,
  WeekdayChips,
} from './EditorFields';
import { errorText, kindKey } from './shared';

export type SeriesEditorMode =
  | { kind: 'create' }
  /** `event` is the occurrence the editor opened ("Þessi og næstu" from it); `from` its slot date. */
  | { kind: 'following'; series: SeriesDTO; from: string; event: EventDTO };

type SeriesChanges = Omit<UpsertSeriesRequest, 'id' | 'startsOn'>;

/**
 * The series fields the editor changed, as a patch for one occurrence that was edited on its own
 * (the series PATCH skips those). Times keep the occurrence's own day. Empty when nothing applies.
 */
function occurrencePatch(series: SeriesDTO, ev: EventDTO, ch: SeriesChanges): EventPatch {
  const p: EventPatch = {};
  if (ch.title !== series.title) p.title = ch.title;
  if (ch.kind !== series.kind) p.kind = ch.kind;
  if ((ch.location ?? null) !== series.location) p.location = ch.location ?? null;
  if ((ch.description ?? null) !== series.description) p.description = ch.description ?? null;
  if (maskFromGroups(ch.audience) !== series.audienceMask) p.audience = ch.audience;
  if (ch.checkinOpensMin !== undefined && ch.checkinOpensMin !== series.checkinOpensMin) p.checkinOpensMin = ch.checkinOpensMin;
  if ((ch.setlistId ?? null) !== series.setlistId) p.setlistId = ch.setlistId ?? null;
  if (ch.startTime !== series.startTime || ch.durationMin !== series.durationMin) {
    const startsAt = dateTimeToMs(todayStr(ev.startsAt), ch.startTime);
    // Like the server's series PATCH: an earlier time must not move a not-yet-started occurrence
    // into the past (it could never be checked into and would count everyone absent).
    if (startsAt > serverNow()) {
      p.startsAt = startsAt;
      p.endsAt = startsAt + ch.durationMin * 60_000;
    }
  }
  return p;
}

interface Form {
  title: string;
  kind: EventKind;
  weekdays: number[];
  intervalWeeks: number;
  startTime: string;
  endTime: string;
  startsOn: string;
  untilOn: string;
  location: string;
  description: string;
  audience: AudienceGroup[];
  checkinOpensMin: number;
  setlistId: string;
}

type Errors = Partial<Record<'title' | 'weekdays' | 'time' | 'startsOn' | 'untilOn' | 'audience', string>>;

function initialForm(mode: SeriesEditorMode, defaultTitle: string): Form {
  if (mode.kind === 'following') {
    const s = mode.series;
    return {
      title: s.title,
      kind: s.kind,
      weekdays: [...s.weekdays].sort((a, b) => a - b),
      intervalWeeks: s.intervalWeeks,
      startTime: s.startTime,
      endTime: hhmmFromMinutes(minutesOf(s.startTime) + s.durationMin),
      startsOn: mode.from,
      untilOn: s.untilOn ?? '',
      location: s.location ?? '',
      description: s.description ?? '',
      audience: groupsFromMask(s.audienceMask),
      checkinOpensMin: s.checkinOpensMin,
      setlistId: s.setlistId ?? '',
    };
  }
  const today = todayStr(serverNow());
  return {
    title: defaultTitle,
    kind: 'practice',
    weekdays: [isoWeekday(today)],
    intervalWeeks: 1,
    startTime: '19:30',
    endTime: '22:00',
    startsOn: today,
    untilOn: '',
    location: '',
    description: '',
    audience: ['all'],
    checkinOpensMin: 30,
    setlistId: '',
  };
}

export function SeriesEditor({
  mode,
  onClose,
  onSaved,
}: {
  mode: SeriesEditorMode;
  onClose: () => void;
  /**
   * Called after a successful save; `newSeriesId` is set when a split created a new series. `from`
   * is the day to show: the first generated rehearsal for a new series, else the edited occurrence's date.
   */
  onSaved?: (r: { seriesId: string; newSeriesId: string | null; from: string }) => void;
}) {
  const t = useT();
  const { loc } = useI18n();
  const toast = useToast();
  const m = useCalendarMutations();
  const qc = useQueryClient();
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState<Form>(() => initialForm(mode, t('calendar.kind.practice')));
  const [titleTouched, setTitleTouched] = useState(mode.kind === 'following');
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [newId] = useState(uuid);
  const busy = checking || m.createSeries.isPending || m.updateSeries.isPending || m.splitSeries.isPending || m.patchEvent.isPending;

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const setKind = (kind: EventKind) =>
    setForm((f) => ({
      ...f,
      kind,
      title: titleTouched ? f.title : t(kindKey(kind)),
    }));

  const validate = (): Errors => {
    const e: Errors = {};
    if (!form.title.trim()) e.title = t('calendar.form.err_title');
    if (form.weekdays.length === 0) e.weekdays = t('calendar.form.err_weekdays');
    if (!/^\d{2}:\d{2}$/.test(form.startTime) || !/^\d{2}:\d{2}$/.test(form.endTime)) e.time = t('calendar.form.err_time');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.startsOn)) e.startsOn = t('calendar.form.err_date');
    if (form.untilOn && form.untilOn < form.startsOn) e.untilOn = t('calendar.form.err_until');
    if (form.audience.length === 0) e.audience = t('calendar.form.err_audience');
    return e;
  };

  /** PATCH keeps the dates and changes all future occurrences — only right when nothing upcoming precedes `from`. */
  const canPatchInPlace = async (series: SeriesDTO, from: string): Promise<boolean> => {
    const sameDates = form.weekdays.join(',') === [...series.weekdays].sort((a, b) => a - b).join(',') && form.intervalWeeks === series.intervalWeeks;
    if (!sameDates) return false;
    if (from <= series.startsOn) return true;
    setChecking(true);
    try {
      return !(await hasUpcomingOccurrenceBefore(qc, series.id, from));
    } finally {
      setChecking(false);
    }
  };

  const save = async () => {
    const e = validate();
    setErrors(e);
    setSubmitError(null);
    if (Object.keys(e).length > 0) return;
    const changes: SeriesChanges = {
      title: form.title.trim(),
      kind: form.kind,
      location: form.location.trim() || null,
      description: form.description.trim() || null,
      audience: form.audience,
      weekdays: form.weekdays,
      intervalWeeks: form.intervalWeeks,
      startTime: form.startTime,
      durationMin: durationBetween(form.startTime, form.endTime),
      untilOn: form.untilOn || null,
      checkinOpensMin: form.checkinOpensMin,
      setlistId: form.setlistId || null,
    };
    try {
      if (mode.kind === 'create') {
        await m.createSeries.mutateAsync({ id: newId, startsOn: form.startsOn, ...changes });
        // Show the day of the first rehearsal actually generated (the start date need not be one of the weekdays).
        const first = firstSeriesDate(
          { weekdays: form.weekdays, intervalWeeks: form.intervalWeeks, startsOn: form.startsOn, untilOn: form.untilOn || null, startTime: form.startTime },
          serverNow(),
        );
        onSaved?.({ seriesId: newId, newSeriesId: null, from: first ?? form.startsOn });
      } else if (await canPatchInPlace(mode.series, mode.from)) {
        const patch: SeriesPatch = { ...changes, startsOn: mode.series.startsOn };
        await m.updateSeries.mutateAsync({ id: mode.series.id, patch });
        // The series PATCH leaves separately edited occurrences alone — including the one opened here.
        const ev = mode.event;
        if (ev.isDetached && ev.status !== 'deleted' && ev.startsAt > serverNow()) {
          const evPatch = occurrencePatch(mode.series, ev, changes);
          if (Object.keys(evPatch).length > 0) await m.patchEvent.mutateAsync({ id: ev.id, patch: evPatch });
        }
        onSaved?.({ seriesId: mode.series.id, newSeriesId: null, from: mode.from });
      } else {
        await m.splitSeries.mutateAsync({ id: mode.series.id, req: { from: mode.from, newSeriesId: newId, changes } });
        onSaved?.({ seriesId: mode.series.id, newSeriesId: newId, from: mode.from });
      }
      toast.show(t('calendar.form.series_saved'), { tone: 'ok' });
      onClose();
    } catch (err) {
      setSubmitError(errorText(t, err));
    }
  };

  const summary = seriesSummary(t, loc, form.weekdays, form.intervalWeeks, form.startTime, form.endTime);

  return (
    <Sheet
      open
      wide
      onClose={onClose}
      title={mode.kind === 'create' ? t('calendar.form.new_series') : t('calendar.form.edit_series')}
      footer={
        <>
          <Button variant="secondary" block onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button block busy={busy} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5 py-2">
        {mode.kind === 'following' ? (
          <p className="rounded-xl bg-[var(--accent-soft)] px-4 py-3 font-medium text-[var(--accent)]">
            {t('calendar.form.from_date', { date: formatDate(mode.from, loc) })}
          </p>
        ) : null}
        <KindChips value={form.kind} onChange={setKind} />
        <TextInput
          label={t('calendar.form.title')}
          value={form.title}
          maxLength={TITLE_MAX}
          error={errors.title}
          onChange={(e) => {
            setTitleTouched(true);
            set('title', e.target.value);
          }}
        />
        <WeekdayChips value={form.weekdays} onChange={(v) => set('weekdays', v)} error={errors.weekdays} />
        <IntervalChips value={form.intervalWeeks} onChange={(v) => set('intervalWeeks', v)} />
        <TimeRangeFields
          start={form.startTime}
          end={form.endTime}
          onStart={(v) => set('startTime', v)}
          onEnd={(v) => set('endTime', v)}
          error={errors.time}
        />
        {summary ? (
          <p className="flex items-center gap-2 text-[16px] font-semibold text-[var(--ink-2)]">
            <Repeat className="size-5 text-[var(--accent)]" aria-hidden />
            {summary}
          </p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            type="date"
            label={t('calendar.form.starts_on')}
            value={form.startsOn}
            disabled={mode.kind === 'following'}
            error={errors.startsOn}
            onChange={(e) => set('startsOn', e.target.value)}
            required
          />
          <TextInput
            type="date"
            label={t('calendar.form.until_on')}
            value={form.untilOn}
            min={form.startsOn}
            hint={t('calendar.form.until_hint')}
            error={errors.untilOn}
            onChange={(e) => set('untilOn', e.target.value)}
          />
        </div>
        <TextInput
          label={t('calendar.form.location')}
          placeholder={t('calendar.form.location_ph')}
          value={form.location}
          maxLength={LOCATION_MAX}
          onChange={(e) => set('location', e.target.value)}
        />
        <TextArea
          label={t('calendar.form.description')}
          placeholder={t('calendar.form.description_ph')}
          value={form.description}
          maxLength={DESCRIPTION_MAX}
          rows={3}
          onChange={(e) => set('description', e.target.value)}
        />
        <AudienceChips value={form.audience} onChange={(v) => set('audience', v)} error={errors.audience} />
        <div className="grid gap-4 sm:grid-cols-2">
          <CheckinOpensSelect value={form.checkinOpensMin} onChange={(v) => set('checkinOpensMin', v)} />
          <SetlistSelect value={form.setlistId} onChange={(v) => set('setlistId', v)} />
        </div>
        {submitError ? <ErrorBox>{submitError}</ErrorBox> : null}
      </div>
    </Sheet>
  );
}
